import { LocationViewModel } from "../../model/LocationViewModel";
import { RoomViewModel } from "../../model/RoomViewModel";
import { ApiResponse } from "../CustomFetch";
import { api } from "./BaseApi";

const getLocationUrl = (id?: string) => "/Location" + (id === undefined ? "" : `/${id}`);

const getRoomUrl = (locationId: string, roomId?: string) =>
  `${getLocationUrl(locationId)}/Room` + (roomId === undefined ? "" : `/${roomId}`);

const locationsApi = api.enhanceEndpoints({ addTagTypes: ["Locations"] }).injectEndpoints({
  endpoints: (builder) => ({
    getLocations: builder.query<LocationViewModel[], void>({
      query: () => getLocationUrl(),
      transformResponse: (response: ApiResponse<LocationViewModel[]>) => response.data,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: "Locations" as const, id })), { type: "Locations", id: "LIST" }]
          : [{ type: "Locations", id: "LIST" }],
    }),
    getLocation: builder.query<LocationViewModel, string>({
      query: getLocationUrl,
      transformResponse: (response: ApiResponse<LocationViewModel>) => response.data,
      providesTags: (_result, _error, arg) => [{ type: "Locations", id: arg }],
    }),
    deleteLocation: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: getLocationUrl(id),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Locations", id: "LIST" },
        { type: "Locations", id: arg },
      ],
    }),
    createLocation: builder.mutation<ApiResponse<string>, LocationViewModel>({
      query: (location: LocationViewModel) => ({
        url: getLocationUrl(),
        body: location,
        headers: { "Content-Type": "application/json" },
        method: "POST",
      }),
      invalidatesTags: [{ type: "Locations", id: "LIST" }],
    }),
    updateLocation: builder.mutation<ApiResponse<void>, LocationViewModel>({
      query: (location: LocationViewModel) => ({
        url: getLocationUrl(),
        body: location,
        headers: { "Content-Type": "application/json" },
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Locations", id: "LIST" },
        { type: "Locations", id: arg.id },
      ],
    }),
    deleteRoom: builder.mutation<ApiResponse<void>, { locationId: string; roomId: string }>({
      query: ({ locationId, roomId }) => ({
        url: getRoomUrl(locationId, roomId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Locations", id: "LIST" },
        { type: "Locations", id: arg.locationId },
      ],
    }),
    createRoom: builder.mutation<ApiResponse<string>, { locationId: string; data: RoomViewModel }>({
      query: ({ locationId, data }) => ({
        url: getRoomUrl(locationId),
        body: data,
        headers: { "Content-Type": "application/json" },
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Locations", id: "LIST" },
        { type: "Locations", id: arg.locationId },
      ],
    }),
    updateRoom: builder.mutation<ApiResponse<void>, { locationId: string; data: RoomViewModel }>({
      query: ({ locationId, data }) => ({
        url: getRoomUrl(locationId),
        body: data,
        headers: { "Content-Type": "application/json" },
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Locations", id: "LIST" },
        { type: "Locations", id: arg.locationId },
      ],
    }),
  }),
});

export const {
  endpoints,
  useGetLocationsQuery,
  useGetLocationQuery,
  useDeleteLocationMutation,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteRoomMutation,
  useCreateRoomMutation,
  useUpdateRoomMutation,
} = locationsApi;
